import { useEnumStore } from "../../store/enumStore";
import { SkillsInterestsListView } from "./SkillsInterestsListView";
import { getSkillLabel, getInterestLabel, getNestedSkillName, getNestedInterestName } from "../../utils/enumHelpers";
import { useTranslation } from "../../i18n";

interface SkillsInterestsListProps {
  items?: number[];
  type?: "skills" | "interests";
  title?: string;
  emptyMessage?: string;
}

/**
 * Smart wrapper component for SkillsInterestsListView
 * Handles data fetching and mapping from Zustand store
 * 
 * @param items - Array of skill/interest IDs
 * @param type - 'skills' or 'interests'
 * @param title - Optional title to display above the list
 * @param emptyMessage - Optional message when list is empty
 */
export const SkillsInterestsList = ({ 
  items = [], 
  type = 'skills',
  title,
  emptyMessage
}: SkillsInterestsListProps) => {
  const getSkillById = useEnumStore((state) => state.getSkillById);
  const getInterestById = useEnumStore((state) => state.getInterestById);
  // The getters read the store directly, so subscribe to the data as well to
  // re-render once the lookups arrive.
  useEnumStore((state) => (type === 'skills' ? state.skills : state.interests));
  // Lookup labels read the current language, so re-render when it changes.
  useTranslation();

  // Choose the right getter function
  const getItemById = type === 'skills' ? getSkillById : getInterestById;

  // Map IDs to objects with nested items
  const mappedItems = items.map((itemId) => {
    const foundItem = getItemById(itemId) as any;
    
    // Get the nested items (skills or interests array)
    const rawNestedItems = type === 'skills' 
      ? foundItem?.skills 
      : foundItem?.interests;
    
    // Map nested items to consistent format using utility functions
    const nestedItems = rawNestedItems?.map((subItem: any) => {
      const subItemName = type === 'skills' 
        ? getNestedSkillName(subItem, 'volunteer')
        : getNestedInterestName(subItem, 'volunteer');
      const subItemId = type === 'skills' 
        ? subItem.skill_id 
        : subItem.interest_id;
      
      return {
        id: subItemId,
        name: subItemName
      };
    }) || [];

    return {
      name: type === 'skills'
        ? getSkillLabel(foundItem, String(itemId))
        : getInterestLabel(foundItem, String(itemId)),
      nestedItems
    };
  });

  // Pass mapped data to presentational component
  return (
    <SkillsInterestsListView 
      items={mappedItems}
      title={title}
      emptyMessage={emptyMessage}
    />
  );
};

